import React, { useState, useEffect } from 'react';
import { useDataCache } from '../contexts/DataCacheContext';
import bookingService from '../services/bookingService';
import configService from '../services/configService';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import Button from './figma/Button';
import Input from './figma/Input';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './BookingCalendar.css';

const localizer = momentLocalizer(moment);

function BookingCalendar({ businessId, cachedStaff, cachedServices, cachedBookings }) {
  const { cache } = useDataCache();
  const [effectiveBusinessId, setEffectiveBusinessId] = useState(businessId || cache.settings?.businessConfig?.business_id);
  const [bookings, setBookings] = useState(cachedBookings || cache.bookings?.bookings || []);
  const [staff, setStaff] = useState(cachedStaff || cache.bookings?.staff || []);
  const [services, setServices] = useState(cachedServices || cache.bookings?.services || []);
  const [serviceFilter, setServiceFilter] = useState('all');
  const today = new Date().toISOString().split('T')[0];
  const fiveDaysLater = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(fiveDaysLater);
  const [loading, setLoading] = useState(!cachedBookings && !cache.bookings);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [prefilledTime, setPrefilledTime] = useState(null);
  const [selectedServiceForSlots, setSelectedServiceForSlots] = useState('');
  const [selectedStaffForSlots, setSelectedStaffForSlots] = useState('');

  useEffect(() => {
    const fetchBusinessId = async () => {
      if (!effectiveBusinessId) {
        try {
          const cognitoUserId = configService.getUserId();
          if (cognitoUserId) {
            const config = await configService.getBusinessConfigByUserId(cognitoUserId);
            setEffectiveBusinessId(config.business_id);
          }
        } catch (error) {
          console.error('Failed to fetch business_id:', error);
        }
      }
    };
    fetchBusinessId();
  }, [effectiveBusinessId]);

  useEffect(() => {
    if (cachedBookings || cache.bookings) {
      if (cachedStaff) setStaff(cachedStaff);
      if (cachedServices) setServices(cachedServices);
      if (cachedBookings) setBookings(cachedBookings);
      setLoading(false);
    } else {
      loadData();
    }
  }, [cachedBookings, cachedStaff, cachedServices, cache.bookings]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      const [bookingsData, staffData, servicesData] = await Promise.all([
        bookingService.listBusinessBookings(params),
        bookingService.listStaff(),
        bookingService.listServices()
      ]);
      
      // Create lookup maps
      const staffMap = {};
      const serviceMap = {};
      staffData.forEach(s => staffMap[s.staff_id] = s.name);
      servicesData.forEach(s => serviceMap[s.service_id] = { name: s.name, duration: s.duration_minutes });
      
      // Enrich bookings with names and duration
      const enrichedBookings = bookingsData.map(b => ({
        ...b,
        staff_name: staffMap[b.staff_id] || b.staff_id,
        service_name: serviceMap[b.service_id]?.name || b.service_id,
        service_duration: serviceMap[b.service_id]?.duration
      }));
      
      setBookings(enrichedBookings);
      setStaff(staffData.filter(s => s.is_active));
      setServices(servicesData);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBooking = async (bookingData) => {
    try {
      const payload = {
        business_id: effectiveBusinessId,
        ...bookingData,
        start_time: new Date(bookingData.start_time).toISOString()
      };

      if (!payload.customer_phone) {
        delete payload.customer_phone;
      }

      console.log('Creating booking with payload:', payload);
      await bookingService.createBooking(payload);
      setShowAddBooking(false);
      setShowAllBookings(true);
      await loadData();
    } catch (error) {
      console.error('Failed to create booking:', error.message);
      alert('Failed to create booking: ' + error.message);
    }
  };

  const handleCancel = async (bookingId) => {
    if (!window.confirm('Cancel this booking?')) return;
    try {
      await bookingService.cancelBooking(bookingId, effectiveBusinessId);
      loadData();
    } catch (error) {
      console.error('Failed to cancel booking:', error.message);
    }
  };

  const formatTime = (value) => {
    if (!value) return 'N/A';
    try {
      // Parse Unix timestamp (string or number) to milliseconds
      const timestamp = parseInt(value) * 1000;
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return 'Invalid Date';
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      return `${dateStr} ${timeStr}`;
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Filter bookings by service first
  const filteredBookings = serviceFilter === 'all' 
    ? bookings 
    : bookings.filter(booking => booking.service_id === serviceFilter);

  // Generate available slots as events
  const generateAvailableSlots = async () => {
    console.log('DEBUG: effectiveBusinessId =', effectiveBusinessId);
    if (!effectiveBusinessId) {
      console.error('No business_id available');
      return [];
    }
    if (!selectedServiceForSlots || !selectedStaffForSlots) return [];
    
    const selectedService = services.find(s => s.service_id === selectedServiceForSlots);
    if (!selectedService) return [];
    
    const serviceDuration = selectedService.duration_minutes || 60;
    const slots = [];
    const today = new Date();
    const endDate = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    for (let d = new Date(today); d <= endDate; d.setDate(d.getDate() + 1)) {
      if (d.getDay() === 0 || d.getDay() === 6) continue;
      
      const dateStr = d.toISOString().split('T')[0];
      
      try {
        const availability = await bookingService.getAvailability(
          effectiveBusinessId,
          {
            staff_id: selectedStaffForSlots,
            service_id: selectedServiceForSlots,
            date: dateStr
          }
        );
        
        const availableSlots = availability.available_slots || [];
        
        availableSlots.forEach(slot => {
          const slotStart = new Date(`${dateStr}T${slot.start}`);
          const slotEnd = new Date(`${dateStr}T${slot.end}`);
          
          if (slotStart > new Date()) {
            slots.push({
              id: `slot-${slotStart.getTime()}`,
              title: '+',
              start: slotStart,
              end: slotEnd,
              isAvailableSlot: true,
              serviceId: selectedServiceForSlots
            });
          }
        });
      } catch (error) {
        console.error('Failed to fetch availability for', dateStr, error);
      }
    }
    return slots;
  };

  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    const fetchSlots = async () => {
      const slots = await generateAvailableSlots();
      setAvailableSlots(slots);
    };
    fetchSlots();
  }, [selectedServiceForSlots, selectedStaffForSlots, filteredBookings]);

  // Use fixed time range to avoid react-big-calendar errors
  const timeRange = {
    min: new Date(2024, 0, 1, 8, 0), // 8am
    max: new Date(2024, 0, 1, 18, 0)  // 6pm
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      {/* Mobile Filters */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-end mb-6 md:hidden">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-900 mb-1">Start Date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={showAllBookings}
              className="disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-900 mb-1">End Date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={showAllBookings}
              className="disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-900 mb-1">Service Filter</label>
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
            >
              <option value="all">All Services</option>
              {services.map(service => (
                <option key={service.service_id} value={service.service_id}>
                  {service.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllBookings}
                onChange={(e) => setShowAllBookings(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-900 whitespace-nowrap">Show All</span>
            </label>
          </div>
        </div>
        <button
          onClick={() => setShowAddBooking(true)}
          className="bg-primary hover:bg-primary-hover text-white px-4 py-2 rounded-sm font-bold whitespace-nowrap min-h-[44px]"
        >
          + New Booking
        </button>
      </div>

      {/* Desktop - Just New Booking Button */}
      <div className="hidden md:flex justify-end gap-2 mb-4">
        <Button
          onClick={loadData}
          disabled={loading}
          variant="outline"
          className="flex items-center gap-2 disabled:opacity-50"
        >
          <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </Button>
        <Button
          onClick={() => setShowAddBooking(true)}
          variant="primary"
        >
          + New Booking
        </Button>
      </div>

      {showAddBooking && (
        <AddBookingForm 
          staff={staff}
          services={services}
          businessId={businessId}
          prefilledTime={prefilledTime}
          onSave={handleCreateBooking} 
          onCancel={() => {
            setShowAddBooking(false);
            setPrefilledTime(null);
          }} 
        />
      )}

      {selectedBooking && (
        <div className="hidden md:block border border-gray-200 rounded p-4 bg-gray-50 mb-4">
          <div className="flex justify-between items-start mb-4">
            <h4 className="text-base font-medium text-gray-900">Booking Details</h4>
            <button onClick={() => setSelectedBooking(null)} className="text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-500">Status</span>
              <div className="mt-1">
                <span className={`px-2 py-1 rounded text-xs ${
                  selectedBooking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  selectedBooking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {selectedBooking.status}
                </span>
              </div>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Time</span>
              <p className="text-gray-900 text-sm mt-1">{formatTime(selectedBooking.start_time)}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Customer</span>
              <p className="text-gray-900 text-sm mt-1">{selectedBooking.customer_name || 'Walk-in'}</p>
              {selectedBooking.customer_phone && !selectedBooking.customer_phone.startsWith('walk-in') && (
                <p className="text-gray-600 text-xs">{selectedBooking.customer_phone}</p>
              )}
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Service</span>
              <p className="text-gray-900 text-sm mt-1">{selectedBooking.service_name || selectedBooking.service_id}</p>
              {selectedBooking.service_duration && (
                <p className="text-gray-600 text-xs">{selectedBooking.service_duration} min</p>
              )}
            </div>
            <div>
              <span className="text-sm font-medium text-gray-500">Staff</span>
              <p className="text-gray-900 text-sm mt-1">{selectedBooking.staff_name || selectedBooking.staff_id}</p>
            </div>
          </div>
          {selectedBooking.status !== 'cancelled' && (
            <Button
              onClick={() => {
                handleCancel(selectedBooking.booking_id);
                setSelectedBooking(null);
              }}
              variant="danger"
              className="mt-4"
            >
              Cancel Booking
            </Button>
          )}
        </div>
      )}

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4">
        {filteredBookings.filter(b => {
          if (showAllBookings) return true;
          const bookingDate = new Date(parseInt(b.start_time) * 1000).toISOString().split('T')[0];
          return bookingDate >= startDate && bookingDate <= endDate;
        }).length === 0 ? (
          <div className="border border-gray-200 rounded p-6 text-center text-gray-500 bg-white">
            No bookings in this date range
          </div>
        ) : (
          filteredBookings.filter(b => {
            if (showAllBookings) return true;
            const bookingDate = new Date(parseInt(b.start_time) * 1000).toISOString().split('T')[0];
            return bookingDate >= startDate && bookingDate <= endDate;
          }).map((booking) => (
            <div key={booking.booking_id} className="border border-gray-200 rounded p-4 bg-white">
              <div className="flex justify-between items-start mb-2">
                <div className="font-semibold text-lg">{formatTime(booking.start_time)}</div>
                <span className={`px-2 py-1 rounded text-xs ${
                  booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                  booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {booking.status}
                </span>
              </div>
              <div className="space-y-1 text-sm">
                <div><span className="font-medium">Customer:</span> {booking.customer_name || 'N/A'}</div>
                {booking.customer_phone && !booking.customer_phone.startsWith('walk-in') && (
                  <div className="text-gray-500">{booking.customer_phone}</div>
                )}
                <div><span className="font-medium">Service:</span> {booking.service_name || booking.service_id} {booking.service_duration && `(${booking.service_duration} min)`}</div>
                <div><span className="font-medium">Staff:</span> {booking.staff_name || booking.staff_id}</div>
              </div>
              {booking.status !== 'cancelled' && (
                <Button
                  onClick={() => handleCancel(booking.booking_id)}
                  variant="danger"
                  fullWidth
                  className="mt-3"
                >
                  Cancel
                </Button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop Calendar View */}
      <div className="hidden md:block border border-gray-200 rounded bg-white p-4" style={{height: '600px'}}>
        <div className="mb-4 flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Show Available Slots For Service:</label>
            <select
              value={selectedServiceForSlots}
              onChange={(e) => setSelectedServiceForSlots(e.target.value)}
              className="w-64 bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
            >
              <option value="">Select a service to see available slots</option>
              {services.map(service => (
                <option key={service.service_id} value={service.service_id}>
                  {service.name} ({service.duration_minutes || 60} min)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Staff Member:</label>
            <select
              value={selectedStaffForSlots}
              onChange={(e) => setSelectedStaffForSlots(e.target.value)}
              className="w-48 bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
            >
              <option value="">Select staff member</option>
              {staff.map(s => (
                <option key={s.staff_id} value={s.staff_id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Calendar
          localizer={localizer}
          events={[
            ...filteredBookings.map(booking => ({
              id: booking.booking_id,
              title: `${booking.customer_name || 'Walk-in'} - ${booking.service_name || booking.service_id}`,
              start: new Date(parseInt(booking.start_time) * 1000),
              end: new Date(parseInt(booking.start_time) * 1000 + (booking.service_duration ? booking.service_duration * 60 * 1000 : 60 * 60 * 1000)),
              resource: booking,
              isAvailableSlot: false
            })),
            ...availableSlots
          ]}
          startAccessor="start"
          endAccessor="end"
          views={['month', 'week', 'day']}
          defaultView="month"
          min={timeRange.min}
          max={timeRange.max}
          onSelectEvent={(event) => {
            if (event.isAvailableSlot) {
              setPrefilledTime(event.start.toISOString().slice(0, 16));
              setShowAddBooking(true);
            } else {
              setSelectedBooking(event.resource);
            }
          }}
          onSelectSlot={(slotInfo) => {
            const slotTime = slotInfo.start.toISOString().slice(0, 16);
            setPrefilledTime(slotTime);
            setShowAddBooking(true);
          }}
          selectable
          slotPropGetter={(date, resourceId) => {
            const now = new Date();
            const isAvailable = date >= now;
            return {
              style: isAvailable ? {
                backgroundColor: '#dcfce7',
                cursor: 'pointer'
              } : {}
            };
          }}
          eventPropGetter={(event) => {
            if (event.isAvailableSlot) {
              return {
                style: {
                  backgroundColor: '#dcfce7',
                  border: '2px dashed #10b981',
                  color: '#059669',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  cursor: 'pointer'
                }
              };
            }
            const booking = event.resource;
            return {
              style: {
                backgroundColor: booking.status === 'confirmed' ? '#10b981' : booking.status === 'cancelled' ? '#ef4444' : '#f59e0b',
                borderRadius: '4px',
                border: 'none',
                color: 'white'
              }
            };
          }}
        />
      </div>
    </div>
  );
}

const AddBookingForm = ({ staff, services, onSave, onCancel, businessId, prefilledTime }) => {
  const [formData, setFormData] = useState({
    staff_id: '',
    service_id: '',
    start_time: prefilledTime || '',
    customer_name: '',
    customer_phone: ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    prefilledTime ? prefilledTime.split('T')[0] : new Date().toISOString().split('T')[0]
  );

  const fetchAvailability = async () => {
    if (!formData.staff_id || !formData.service_id || !selectedDate) return;
    
    setLoadingSlots(true);
    try {
      const slots = await bookingService.getAvailability({
        business_id: businessId,
        staff_id: formData.staff_id,
        service_id: formData.service_id,
        date: selectedDate
      });
      setAvailableSlots(slots.available_slots || []);
    } catch (error) {
      console.error('Failed to fetch availability:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    fetchAvailability();
  }, [formData.staff_id, formData.service_id, selectedDate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="border border-gray-200 rounded p-4 bg-gray-50 mb-4">
      <h4 className="text-base font-medium text-gray-900 mb-4">Add New Booking</h4>
      
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Staff</label>
              <select
                value={formData.staff_id}
                onChange={(e) => setFormData({...formData, staff_id: e.target.value})}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              >
                <option value="">Select Staff</option>
                {staff.map(s => <option key={s.staff_id} value={s.staff_id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Service</label>
              <select
                value={formData.service_id}
                onChange={(e) => setFormData({...formData, service_id: e.target.value})}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              >
                <option value="">Select Service</option>
                {services.map(s => <option key={s.service_id} value={s.service_id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Date</label>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 mb-1">Available Time Slots</label>
            {loadingSlots ? (
              <div className="text-center py-4 text-gray-500">Loading available times...</div>
            ) : availableSlots.length > 0 ? (
              <select
                value={formData.start_time}
                onChange={(e) => setFormData({...formData, start_time: e.target.value})}
                className="w-full bg-white border border-gray-200 rounded px-3 py-2 text-sm text-gray-900"
                required
              >
                <option value="">Select a time slot</option>
                {availableSlots.map((slot, index) => {
                  const datetime = `${selectedDate}T${slot.start}`;
                  return (
                    <option key={index} value={datetime}>
                      {slot.start} - {slot.end} ({slot.staff_name})
                    </option>
                  );
                })}
              </select>
            ) : (
              <div className="text-center py-4 text-gray-500 border border-gray-200 rounded">
                {formData.staff_id && formData.service_id ? 'No available time slots for this date' : 'Select staff and service first'}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Customer Name</label>
              <Input
                type="text"
                placeholder="Optional"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1">Customer Phone</label>
              <Input
                type="tel"
                placeholder="Optional for walk-ins"
                value={formData.customer_phone}
                onChange={(e) => setFormData({...formData, customer_phone: e.target.value})}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
          >
            Create Booking
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BookingCalendar;
